'use client';

import type { EventName } from './events';

const KEY = 'quantifyr_tab';

/**
 * A random id that lives for one tab.
 *
 * Kept in sessionStorage rather than a cookie or localStorage on purpose: it
 * is gone when the tab closes, it never travels to another site, and it is not
 * sent with requests. All it buys is not counting one person's five reloads as
 * five visitors — and it is worth exactly that much and no more.
 */
function tabId(): string | null {
  try {
    let id = sessionStorage.getItem(KEY);
    if (!id) {
      id = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    // Private browsing, or storage the visitor has turned off. Counting still
    // works, it just cannot dedupe — which is a better outcome than the page
    // throwing on the way in.
    return null;
  }
}

/**
 * Records that something happened. Never throws, never blocks.
 *
 * Analytics must not be able to break a page or slow it down: every call is
 * fire-and-forget, failures are swallowed, and `keepalive` lets the request
 * finish even when it was the click that navigated away — which is exactly the
 * case for the CTA, the one event most worth not losing.
 */
export function track(name: EventName, path?: string): void {
  if (typeof window === 'undefined') return;
  try {
    void fetch('/api/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name,
        path: path ?? window.location.pathname,
        session: tabId(),
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* counting is never worth an error in front of a visitor */
  }
}
